import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { zodResolver } from '@hookform/resolvers/zod';
import { parseISO } from 'date-fns';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useEstablecimiento } from '@/features/establecimientos/hooks/useEstablecimientos';
import { useProfesional } from '@/features/profesionales/hooks/useProfesionales';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage } from '@/lib/apiError';
import { formatDateAR } from '@/utils/formatters';

import { protocoloEditSchema, type ProtocoloEditValues } from '../schemas';
import type { Protocolo } from '../types';
import { useUpdateProtocolo } from '../hooks/useProtocolos';

interface DatosGeneralesSectionProps {
  protocolo: Protocolo;
  canEdit: boolean;
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Box>
  );
}

export function DatosGeneralesSection({
  protocolo,
  canEdit,
}: DatosGeneralesSectionProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const notify = useNotify();
  const updateMutation = useUpdateProtocolo();

  const { data: profesional, isLoading: loadingProfesional } = useProfesional(
    protocolo.uid_profesional
  );
  const { data: establecimiento, isLoading: loadingEstablecimiento } = useEstablecimiento(
    protocolo.uid_establecimiento
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ProtocoloEditValues>({
    resolver: zodResolver(protocoloEditSchema),
    defaultValues: {
      fecha_recepcion: parseISO(protocolo.fecha_recepcion),
      fecha_protocolo: parseISO(protocolo.fecha_protocolo),
    },
  });

  function handleStartEdit(): void {
    reset({
      fecha_recepcion: parseISO(protocolo.fecha_recepcion),
      fecha_protocolo: parseISO(protocolo.fecha_protocolo),
    });
    setIsEditing(true);
  }

  function handleCancelEdit(): void {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      updateMutation.reset();
      setIsEditing(false);
    }
  }

  function handleConfirmDiscard(): void {
    updateMutation.reset();
    setShowDiscardDialog(false);
    setIsEditing(false);
  }

  async function handleSave(values: ProtocoloEditValues): Promise<void> {
    try {
      await updateMutation.mutateAsync({
        uid: protocolo.uid_protocolo,
        data: {
          fecha_recepcion: values.fecha_recepcion?.toISOString() ?? protocolo.fecha_recepcion,
          fecha_protocolo: values.fecha_protocolo?.toISOString() ?? protocolo.fecha_protocolo,
        },
      });
      notify.success('Protocolo actualizado');
      setIsEditing(false);
    } catch {
      // Error shown via updateMutation.error in the Alert
    }
  }

  const isSaving = updateMutation.isPending;
  const profesionalNombre = loadingProfesional ? null : (profesional?.nombre_apellido ?? '—');
  const establecimientoNombre = loadingEstablecimiento
    ? null
    : (establecimiento?.nombre ?? '—');

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Typography variant="h6">Datos generales</Typography>
        {canEdit && !isEditing && (
          <Button startIcon={<EditIcon />} size="small" onClick={handleStartEdit}>
            Editar
          </Button>
        )}
      </Stack>

      {isEditing ? (
        /* Edit mode */
        <>
          {updateMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getErrorMessage(updateMutation.error)}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            {/* Read-only: N° protocolo */}
            <Tooltip title="El número de protocolo es inmutable">
              <span>
                <TextField
                  label="N° de protocolo"
                  value={protocolo.numero_protocolo}
                  fullWidth
                  disabled
                  size="small"
                />
              </span>
            </Tooltip>

            {/* Read-only: Profesional */}
            <Tooltip title="El profesional no puede modificarse desde aquí">
              <span>
                <TextField
                  label="Profesional"
                  value={profesionalNombre ?? 'Cargando...'}
                  fullWidth
                  disabled
                  size="small"
                />
              </span>
            </Tooltip>

            {/* Read-only: Establecimiento */}
            <Tooltip title="El establecimiento no puede modificarse desde aquí">
              <span>
                <TextField
                  label="Establecimiento"
                  value={establecimientoNombre ?? 'Cargando...'}
                  fullWidth
                  disabled
                  size="small"
                />
              </span>
            </Tooltip>

            {/* Editable: Fecha recepción */}
            <Controller
              control={control}
              name="fecha_recepcion"
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Fecha de recepción"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                />
              )}
            />

            {/* Editable: Fecha protocolo */}
            <Controller
              control={control}
              name="fecha_protocolo"
              render={({ field, fieldState }) => (
                <DatePicker
                  label="Fecha del protocolo"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                    },
                  }}
                />
              )}
            />

          </Box>

          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
            <Button onClick={handleCancelEdit} disabled={isSaving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={isSaving || !isDirty}
              onClick={() => void handleSubmit(handleSave)()}
            >
              {isSaving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </Stack>
        </>
      ) : (
        /* View mode */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          <FieldRow label="N° de protocolo" value={protocolo.numero_protocolo} />

          <FieldRow
            label="Profesional"
            value={
              loadingProfesional ? <Skeleton width={140} /> : profesionalNombre
            }
          />

          <FieldRow
            label="Establecimiento"
            value={
              loadingEstablecimiento ? <Skeleton width={140} /> : establecimientoNombre
            }
          />

          <FieldRow label="Fecha de recepción" value={formatDateAR(protocolo.fecha_recepcion)} />

          <FieldRow label="Fecha del protocolo" value={formatDateAR(protocolo.fecha_protocolo)} />

          <FieldRow
            label="Estado"
            value={protocolo.informe_final_emitido ? 'Cerrado' : 'En curso'}
          />

        </Box>
      )}

      {/* Confirm discard dialog */}
      <Dialog open={showDiscardDialog} onClose={() => setShowDiscardDialog(false)} maxWidth="xs">
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Descartar los cambios sin guardar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Seguir editando</Button>
          <Button onClick={handleConfirmDiscard} color="error">
            Descartar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
